/**
 * Scheduled user attestations (CUR-858).
 *
 * A schedule sends a user attestation on a chosen day, once or on a cadence,
 * exactly as a manual send would. These constants are shared so the browser
 * gets labels and bounds without importing anything server-side.
 */

/** PostHog flag gating the whole feature — server procedures, sweep and client routes. */
export const SCHEDULED_USER_ATTESTATION_FLAG = 'enable-scheduled-attestations';

/**
 * How often a schedule re-sends, with the labels shown in the UI. Mirrors the
 * `ScheduledUserAttestationFrequency` Prisma enum. Order is the dropdown order
 * the ticket specifies.
 */
export const SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    FORTNIGHTLY: 'Fortnightly',
    MONTHLY: 'Monthly',
    EVERY_3_MONTHS: 'Every 3 months',
    EVERY_6_MONTHS: 'Every 6 months',
    ANNUALLY: 'Annually',
} as const satisfies Record<string, string>;

export type ScheduledUserAttestationFrequencyKey = keyof typeof SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS;

export const SCHEDULED_USER_ATTESTATION_FREQUENCIES = Object.keys(
    SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS,
) as readonly ScheduledUserAttestationFrequencyKey[];

/** Register status labels. Mirrors the `ScheduledUserAttestationStatus` Prisma enum. */
export const SCHEDULED_USER_ATTESTATION_STATUS_LABELS = {
    ACTIVE: 'Active',
    DISABLED: 'Disabled',
    COMPLETED: 'Completed',
} as const satisfies Record<string, string>;

export type ScheduledUserAttestationStatusKey = keyof typeof SCHEDULED_USER_ATTESTATION_STATUS_LABELS;

/** Who a schedule targets. Mirrors the `ScheduledUserAttestationRecipientMode` Prisma enum. */
export const SCHEDULED_USER_ATTESTATION_RECIPIENT_MODES = ['USERS', 'HANDLING_PARTIES', 'ALL_ACTIVE'] as const;

export type ScheduledUserAttestationRecipientModeKey = (typeof SCHEDULED_USER_ATTESTATION_RECIPIENT_MODES)[number];

export const SCHEDULED_USER_ATTESTATION_LIMITS = {
    /** Explicitly targeted users (USERS mode). */
    MAX_TARGET_USERS: 500,
    /** Targeted handling parties (HANDLING_PARTIES mode). */
    MAX_TARGET_HANDLING_PARTIES: 100,
    /** Per-user exclusions (HANDLING_PARTIES / ALL_ACTIVE modes). */
    MAX_EXCLUDED_USERS: 500,
    /**
     * Soft per-organisation cap. Schedules are deletable config, so this is a
     * sanity bound on list and sweep cost rather than a budget.
     */
    MAX_SCHEDULES_PER_ORG: 200,
    /** Minutes from local midnight at which every occurrence fires (09:00). */
    SEND_MINUTE_OF_DAY: 9 * 60,
} as const;
